/**
 * <Header> — a header bar with a centered title + optional action slots (molecule, L0.1).
 *
 * The generic maison header, reused by both full-screen pages and the `<Modal>` shell (hence the
 * neutral name, not `PageHeader`). Renders a raised surface bar with a bottom border, a centered
 * `<Title>`, and inline `left` · title · `right` slots.
 *
 * Centering: the two slots are equal-flex containers, so the title sits exactly in the middle of the
 * bar whatever the slots hold — including nothing at all. Consumers must NOT pass a spacer `<View>`
 * to balance a missing `right`; the layout already handles it. A long title ellipsizes (the slots
 * have a zero flex-basis, so shrinking is absorbed by the title, never by the action buttons).
 *
 * Width:
 *  - `fullWidth={false}` (default): sizes to `DimensionsContext.width` — the full-screen page header.
 *  - `fullWidth`: spans `100%` of its container — used when embedded in a narrower block (e.g. a modal panel).
 *
 * Casse : le titre s'écrit comme il est donné, en casse normale, partout — pages, feuilles, têtes
 * d'étape (Wajih, 20/10, parent compris : les capitales « faisaient tache »). Il n'y a plus de prop
 * pour les imposer.
 *
 * `steps` en fait la tête d'une étape de parcours (`<Modal steps>`, `<CancelFlowStep>`) : la
 * question de l'étape en titre, qui passe à la ligne au lieu d'être coupée, le retour à gauche,
 * la croix à droite, et la barre de progression à la place du filet.
 *
 * Pure layout: composes `<Title>` + slots, tokens only, no hex.
 */
import React, { useContext } from "react"
import { Dimensions, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Title } from "../../atoms/typography/Title"
import { Row } from "../../atoms/containers/Row"
import { DimensionsContext } from "../../../common/utils/mobileUtils"

/**
 * Largeur de chaque fente de la tête d'étape, pleine ou vide : la cible tactile de 44 px. Les deux
 * fentes se font face, donc le titre reste au milieu et ne passe jamais sous une action.
 */
const STEP_ACTION_SIZE = 44

/** Épaisseur de la barre de progression — un trait, pas une jauge : elle tient la place du filet. */
const PROGRESS_HEIGHT = 3

export type HeaderSteps = {
    /** Étape courante, à partir de 1. */
    step: number
    /** Nombre d'étapes du parcours. À 1, ni barre ni annonce : il n'y a pas de progression à dire. */
    total: number
    /** Absent → pas de flèche de retour (première étape, accusé). */
    onBack?: () => void
    /** Absent → pas de croix. */
    onClose?: () => void
}

export type HeaderProps = {
    title: string
    /** Leading action slot (e.g. a close `<IconButton>`). */
    left?: React.ReactNode
    /** Trailing action slot (e.g. a refresh `<IconButton>`). */
    right?: React.ReactNode
    /** Span `100%` of the container instead of the screen width. Defaults to false. */
    fullWidth?: boolean
    /**
     * La tête d'une étape de parcours : le titre (la question de l'étape) passe à la ligne au lieu
     * d'être coupé, la flèche de retour à gauche, la croix à droite, et une barre fine collée
     * sous la barre, à la place du filet, dès qu'il y a plus d'une étape — « Étape 2 sur 3 » est
     * dit au lecteur d'écran, pas écrit. `left` / `right` n'y ont pas cours. Absent → la barre
     * d'avant.
     */
    steps?: HeaderSteps
    testID?: string
}

export function Header(props: HeaderProps) {
    const { title, left, right, fullWidth = false, steps, testID } = props
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const progress = steps != null && steps.total > 1 ? steps : null

    return (
        <View testID={testID} style={styles.container(progress != null, steps != null)}>
            <Row justify="center" style={styles.band(steps != null)}>
                <Row style={styles.header(fullWidth, dimensions.width)} gap="3" align="center">
                    {steps != null ? (
                        <>
                            <View style={styles.stepAction}>
                                {steps.onBack != null && (
                                    <StepAction
                                        iconName="chevron-left"
                                        onPress={steps.onBack}
                                        label="Revenir à l'étape précédente"
                                    />
                                )}
                            </View>
                            <Title size="text-xl" center style={styles.stepTitle}>
                                {title}
                            </Title>
                            <View style={styles.stepAction}>
                                {steps.onClose != null && (
                                    <StepAction iconName="times" onPress={steps.onClose} label="Fermer" />
                                )}
                            </View>
                        </>
                    ) : (
                        <>
                            <View style={styles.slot}>{left}</View>
                            <Title numberOfLines={1} center style={styles.title}>
                                {title}
                            </Title>
                            <View style={[styles.slot, styles.slotEnd]}>{right}</View>
                        </>
                    )}
                </Row>
            </Row>
            {progress != null && <StepProgress step={progress.step} total={progress.total} />}
        </View>
    )
}

function StepAction(props: { iconName: "chevron-left" | "times"; onPress: () => void; label: string }) {
    return (
        <IconButton
            iconName={props.iconName}
            appearance="transparent"
            iconColor="primary"
            size={STEP_ACTION_SIZE}
            iconSize={20}
            onPress={props.onPress}
            accessibilityLabel={props.label}
        />
    )
}

function StepProgress({ step, total }: Pick<HeaderSteps, "step" | "total">) {
    const current = Math.max(1, Math.min(total, step))
    const label = `Étape ${current} sur ${total}`

    return (
        <View
            accessible
            role="progressbar"
            aria-label={label}
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={current}
            aria-valuetext={label}
        >
            <ProgressBar
                value={(current / total) * 100}
                color="role"
                trackColor="border"
                height={PROGRESS_HEIGHT}
                radius="full"
            />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /**
     * La barre de progression tient la place du filet : l'un ou l'autre, jamais les deux. La tête
     * d'étape ne pousse pas le corps : l'écart jusqu'à la première ligne est celui de la page ou de
     * la feuille qui la porte, un seul espacement de section — sinon marge et remplissage
     * s'additionnaient en 40 px de vide sous la barre.
     */
    container: (withProgress: boolean, stepMode: boolean) => ({
        backgroundColor: theme.colors["surface-raised"],
        borderColor: theme.colors.border,
        borderBottomWidth: withProgress ? 0 : 1,
        marginBottom: stepMode ? 0 : theme.spacing["5"],
    }),
    /** Une question peut tenir sur deux lignes : la tête d'étape se donne un peu plus d'air. */
    band: (stepMode: boolean) => ({
        paddingVertical: theme.spacing[stepMode ? "2" : "1"],
    }),
    header: (fullWidth: boolean, width: number) => ({
        width: fullWidth ? "100%" : width,
    }),
    /** Zero flex-basis + equal grow ⇒ both slots always claim the same width, centering the title. */
    slot: {
        flexGrow: 1,
        flexBasis: 0,
        flexDirection: "row" as const,
        alignItems: "center" as const,
    },
    slotEnd: {
        justifyContent: "flex-end" as const,
    },
    /** Shrinks (and ellipsizes) before the slots do — their zero basis absorbs no shrink. */
    title: {
        flexShrink: 1,
    },
    /** La fente d'une tête d'étape : la place d'une action, qu'elle soit là ou non. */
    stepAction: {
        width: STEP_ACTION_SIZE,
        height: STEP_ACTION_SIZE,
    },
    /** Entre deux fentes fixes, la question prend tout le milieu et y passe à la ligne. */
    stepTitle: {
        flex: 1,
    },
}))
